import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
} from "@heroui/react";
import { CalendarCheck, Check, Warning, X } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { EditableInputComponent } from "components/index";
import ImprovedTimeslotManager from "screens/buildings/components/rentalTab/calendar/improvedTimeslotManager";
import { UnitDTO } from "shared/types/unit";
import { useState, useEffect, useMemo } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { CustomToggle } from "../../ui/CustomToggle";
import { usePlannedVisitsCheck } from "../hooks/usePlannedVisitsCheck";
import { PlannedVisitsWarningModal } from "./PlannedVisitsWarningModal";
import type { CandidateInfo } from "components/candidates/types";

interface TimeslotModalProps {
  isOpen: boolean;
  onClose: () => void;
  unit: UnitDTO | null;
  onUnitUpdate?: (updatedUnit: Partial<UnitDTO>) => void;
}



export const TimeslotModal = ({ 
  isOpen, 
  onClose, 
  unit, 
  onUnitUpdate 
}: TimeslotModalProps) => {
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [localVisibleInCalendar, setLocalVisibleInCalendar] = useState(false);
  const [advertisedRentalPrice, setAdvertisedRentalPrice] = useState("");
  const [advertisedProvisions, setAdvertisedProvisions] = useState("");
  const { check: checkPlannedVisits } = usePlannedVisitsCheck();
  // Niet-null zolang de waarschuwing over nog geplande bezoeken openstaat
  const [plannedVisits, setPlannedVisits] = useState<CandidateInfo[] | null>(null);
  // De tijdslotbeheerder toont zijn eigen waarschuwing; dan verbergen we deze modal
  const [isChildOverlayOpen, setIsChildOverlayOpen] = useState(false);

  // Clear and reset state when unit changes or modal opens/closes
  useEffect(() => {
    if (unit && isOpen) {
      setLocalVisibleInCalendar(unit.visibleInCalendar || false);
      setAdvertisedRentalPrice(unit.advertisedRentalPrice?.toString() || "");
      setAdvertisedProvisions(unit.advertisedProvisions?.toString() || "");
    } else if (!isOpen) {
      // Clear state when modal closes
      setLocalVisibleInCalendar(false);
      setAdvertisedRentalPrice("");
      setAdvertisedProvisions("");
      setPlannedVisits(null);
    }
  }, [unit, isOpen]);
  
  // Validation - check if required fields are filled
  const hasRequiredFields = useMemo(() => {
    return advertisedRentalPrice.trim() !== "" && advertisedProvisions.trim() !== "";
  }, [advertisedRentalPrice, advertisedProvisions]);
  
  // ERV comparison warnings
  const priceComparison = useMemo(() => {
    if (!unit?.estimatedRentalValue || !advertisedRentalPrice.trim()) {
      return null;
    }
    
    const advertisedPrice = parseFloat(advertisedRentalPrice.toString().replace(",", "."));
    const erv = unit.estimatedRentalValue;
    
    if (isNaN(advertisedPrice)) return null;
    
    if (advertisedPrice > erv) {
      return {
        type: "higher",
        message: `Geafficheerde prijs (€${advertisedPrice}) is hoger dan ERV (€${erv})`,
        bgColor: "bg-moss-primary/10",
        textColor: "text-moss-primary"
      };
    } else if (advertisedPrice < erv) {
      return {
        type: "lower",
        message: `Geafficheerde prijs (€${advertisedPrice}) is lager dan ERV (€${erv})`,
        bgColor: "bg-brick-primary/10",
        textColor: "text-brick-primary"
      };
    }
    return null;
  }, [advertisedRentalPrice, unit?.estimatedRentalValue]);
  
  // Provisions comparison warnings
  const provisionsComparison = useMemo(() => {
    if (!unit?.estimatedCommonCosts || !advertisedProvisions.trim()) {
      return null;
    }
    
    const advertisedProv = parseFloat(advertisedProvisions.toString().replace(",", "."));
    const estimatedProv = unit.estimatedCommonCosts;
    
    if (isNaN(advertisedProv)) return null;
    
    if (advertisedProv > estimatedProv) {
      return {
        type: "higher",
        message: `Geafficheerde provisies (€${advertisedProv}) is hoger dan geschat (€${estimatedProv})`,
        bgColor: "bg-moss-primary/10",
        textColor: "text-moss-primary"
      };
    } else if (advertisedProv < estimatedProv) {
      return {
        type: "lower",
        message: `Geafficheerde provisies (€${advertisedProv}) is lager dan geschat (€${estimatedProv})`,
        bgColor: "bg-brick-primary/10",
        textColor: "text-brick-primary"
      };
    }
    return null;
  }, [advertisedProvisions, unit?.estimatedCommonCosts]);

  /**
   * De unit offline halen verwijdert de afsprakenkalender, maar annuleert reeds
   * geboekte bezoeken niet (die staan los van de tijdsloten). Eerst waarschuwen.
   */
  const handleSave = async () => {
    if (!unit?.id) return;

    const isUnpublishing = unit.visibleInCalendar === true && localVisibleInCalendar === false;
    if (isUnpublishing) {
      setIsLoading(true);
      const visits = await checkPlannedVisits(unit.id as string);
      setIsLoading(false);

      // visits === null: controle faalde (toast getoond) — niet blokkeren.
      if (visits && visits.length > 0) {
        setPlannedVisits(visits);
        return;
      }
    }

    await persistCalendarSettings();
  };

  const persistCalendarSettings = async () => {
    if (!unit?.id) return;

    setIsLoading(true);
    try {
      const updates = {
        visibleInCalendar: localVisibleInCalendar,
        advertisedRentalPrice: advertisedRentalPrice ? Math.ceil(parseFloat(advertisedRentalPrice.toString().replace(",", "."))) : unit.advertisedRentalPrice,
        advertisedProvisions: advertisedProvisions ? Math.ceil(parseFloat(advertisedProvisions.toString().replace(",", "."))) : unit.advertisedProvisions,
      };

      const response = await fetchWithAuth(
        `${API_URL}/units/update/${unit.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updates),
        },
        session,
      );

      if (!response.ok) {
        throw new Error(`Unit update failed: ${response.status}`);
      }

      toast.success("Afsprakenkalender instellingen opgeslagen!");
      setPlannedVisits(null);
      onUnitUpdate?.(updates);
      onClose();
    } catch (error) {
      console.error("Failed to save calendar settings:", error);
      toast.error("Fout bij opslaan instellingen!");
      Sentry.captureException(error, {
        tags: { feature: "timeslots", method: "persistCalendarSettings" },
        extra: { unitId: unit.id, visibleInCalendar: localVisibleInCalendar },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // State will be cleared by useEffect when isOpen becomes false
    onClose();
  };

  return (
    <>
    {/* Tijdelijk verbergen zolang de waarschuwing openstaat: twee overlays over
        elkaar (HeroUI Modal + Radix Dialog) botsen op focus trapping. */}
    <Modal
      isOpen={isOpen && !plannedVisits && !isChildOverlayOpen}
      onClose={handleClose}
      size="5xl"
      shouldBlockScroll={true}
      isDismissable={true}
      scrollBehavior="inside"
      backdrop="blur"
      className="p-8"
    >
      <ModalContent className="flex flex-col gap-8">
        <ModalHeader className="flex items-center justify-between p-0 font-normal">
          <div className="flex items-center gap-1">
            <CalendarCheck size={22} weight="duotone" />
            <p>Afsprakenkalender voor {unit?.id}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-secondary-mid">
              Tijdslots worden direct opgeslagen
            </span>
            <ButtonNew
              onClick={handleSave}
              icon={<Check size={18} weight="bold" />}
              label="Publicatie instellingen bewaren"
              variant="primaryMoss"
              isDisabled={isLoading}
            />
          </div>
        </ModalHeader>
        
        <ModalBody className="flex flex-col gap-6 px-0">
          <div className="divide-y divide-neutral-primary/15 rounded-md border border-neutral-primary/15 bg-white">
            {/* Publication Toggle */}
            <div className="flex flex-wrap items-center justify-between p-2.5 text-sm font-bold">
              <div className="flex items-center gap-2">
                <span>Staat gepubliceerd in afsprakenkalender</span>
                {!hasRequiredFields && (
                  <div className="flex items-center gap-1 text-brick-primary">
                    <Warning size={16} weight="bold" />
                    <span className="text-xs font-normal">Vereist: huurprijs en provisies</span>
                  </div>
                )}
              </div>
              <CustomToggle
                isSelected={localVisibleInCalendar}
                onValueChange={setLocalVisibleInCalendar}
                disabled={!hasRequiredFields}
                size="md"
              />
            </div>

            {/* Advertised Rental Price */}
            <div className="flex flex-col gap-2 p-2.5">
              <div className="flex flex-wrap items-center justify-between text-sm font-bold">
                <div className="flex flex-col gap-1">
                  <span>Geafficheerde huurprijs per maand in EUR *</span>
                  {unit?.estimatedRentalValue && (
                    <span className="text-xs font-normal text-neutral-secondary-mid">
                      ERV: €{unit.estimatedRentalValue}
                    </span>
                  )}
                </div>
                <EditableInputComponent
                  label="Geafficheerde huurprijs per maand in EUR"
                  editMode={true}
                  value={advertisedRentalPrice}
                  isLoading={false}
                  onChange={(value: string) => setAdvertisedRentalPrice(value)}
                />
              </div>
              {priceComparison && (
                <div className={`flex items-center gap-2 rounded-md p-2 ${priceComparison.bgColor} ${priceComparison.textColor}`}>
                  <Warning size={16} weight="bold" />
                  <span className="text-xs">{priceComparison.message}</span>
                </div>
              )}
            </div>

            {/* Advertised Provisions */}
            <div className="flex flex-col gap-2 p-2.5">
              <div className="flex flex-wrap items-center justify-between text-sm font-bold">
                <div className="flex flex-col gap-1">
                  <span>Geafficheerde provisies per maand in EUR *</span>
                  {unit?.estimatedCommonCosts && (
                    <span className="text-xs font-normal text-neutral-secondary-mid">
                      Geschat: €{unit.estimatedCommonCosts}
                    </span>
                  )}
                </div>
                <EditableInputComponent
                  label="Geafficheerde provisies per maand in EUR"
                  editMode={true}
                  value={advertisedProvisions}
                  isLoading={false}
                  onChange={(value: string) => setAdvertisedProvisions(value)}
                />
              </div>
              {provisionsComparison && (
                <div className={`flex items-center gap-2 rounded-md p-2 ${provisionsComparison.bgColor} ${provisionsComparison.textColor}`}>
                  <Warning size={16} weight="bold" />
                  <span className="text-xs">{provisionsComparison.message}</span>
                </div>
              )}
            </div>

            {/* Timeslot Manager */}
            {unit?.id && hasRequiredFields ? (
              <div className="border-t border-neutral-primary/10 pt-4">
                <ImprovedTimeslotManager
                  unitId={unit.id as string}
                  onBlockingOverlayChange={setIsChildOverlayOpen}
                />
              </div>
            ) : (
              <div className="p-6 text-center border border-brick-primary/20 rounded-lg bg-brick-primary/5">
                <div className="flex flex-col items-center gap-3">
                  <X size={24} className="text-brick-primary" />
                  <div className="text-center">
                    <p className="font-medium text-brick-primary">Tijdslots niet beschikbaar</p>
                    <p className="text-sm text-neutral-secondary-mid mt-1">
                      Vul eerst de geafficheerde huurprijs en provisies in om tijdslots te beheren
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>

    <PlannedVisitsWarningModal
      isOpen={!!plannedVisits}
      visits={plannedVisits ?? []}
      action="unpublish"
      isProcessing={isLoading}
      onCancel={() => setPlannedVisits(null)}
      onConfirm={persistCalendarSettings}
    />
    </>
  );
};
